import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { workoutService, WorkoutHistoryItem, WorkoutLogSchema } from "../services/workout";
import { isAuthenticated } from "@/utils/auth";

export function useWorkoutHistory() {
  const [workoutHistory, setWorkoutHistory] = useState<WorkoutHistoryItem[]>([]);
  const [rawWorkoutLogs, setRawWorkoutLogs] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [debugError, setDebugError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const navigate = useNavigate();

  const fetchWorkoutHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setDebugError(null);
    try {
      const authenticated = await isAuthenticated();
      if (!authenticated) {
        navigate("/login", {
          state: {
            from: "/workout-history",
            message: "Please log in to view your workout history",
          },
        });
        return;
      }

      try {
        const rawLogs = await workoutService.getRawWorkoutLogs();
        setRawWorkoutLogs(rawLogs);
      } catch (rawError) {
        setDebugError(
          rawError instanceof Error ? rawError : new Error(String(rawError))
        );
      }

      const history = await workoutService.getWorkoutHistory();
      const formattedHistory: WorkoutHistoryItem[] = history
        .map((item) => {
          let log;
          try {
            log = WorkoutLogSchema.parse(item);
          } catch (e) {
            return null;
          }
          // Always extract exercise_results and exercises from workout_snapshot
          const snapshot = log.workout_snapshot;
          let exercises: any[] = [];
          let exerciseResults: Record<string, any> = {};
          if (snapshot && typeof snapshot === "object") {
            exercises = Array.isArray(snapshot.exercises) ? snapshot.exercises : [];
            exerciseResults = typeof snapshot.exercise_results === "object" && snapshot.exercise_results !== null ? snapshot.exercise_results : {};
          }
          const exercises_completed = exercises.filter(
            (ex: any) => exerciseResults[ex.id]?.completed === true
          ).length;
          return {
            id: log.id,
            date: new Date(log.created_at ?? log.completed_at ?? Date.now()),
            workout_name: log.workout_name ?? "Unnamed Workout",
            status: log.status,
            exercises_completed,
            total_exercises: exercises.length,
            duration: log.duration,
            workout_id: log.workout_id,
          };
        })
        .filter(Boolean) as WorkoutHistoryItem[];
      setWorkoutHistory(formattedHistory);
      setDataLoaded(true);
    } catch (err) {
      setError("Failed to load workout history");
      setDebugError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  return {
    workoutHistory,
    rawWorkoutLogs,
    error,
    debugError,
    isLoading,
    dataLoaded,
    fetchWorkoutHistory,
  };
}
